import React from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS } from '../results/ResultsDesignTokens';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
    Tooltip, ErrorBar, ReferenceLine,
} from 'recharts';

const SCENARIO_NAMES = {
    A: 'Perfect',
    B: 'Black-box',
    C: 'Cold-start',
    D: '3-day FT',
    E: '7-day FT',
};

const SCENARIO_COLORS = ['#00ff88', '#ffaa00', '#9b59b6', '#4488ff', '#00d4aa'];

export default function ScenarioPopChart({ scenarioResults }) {
    if (!scenarioResults || scenarioResults.length === 0) return null;

    const data = scenarioResults.map((sr, i) => ({
        name: SCENARIO_NAMES[sr.scenario_id] || sr.scenario_name || sr.scenario_id,
        pop_mean: sr.pop_mean,
        pop_std: sr.pop_std,
        n_buildings: sr.n_buildings,
        color: SCENARIO_COLORS[i % SCENARIO_COLORS.length],
    }));

    return (
        <Box>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: COLORS.text.muted,
                mb: 1,
            }}>
                PoP by Forecast Scenario (%)
            </Typography>
            <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <XAxis
                            dataKey="name"
                            tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[60, 100]}
                            tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                            tickLine={false}
                            label={{
                                value: 'PoP %',
                                angle: -90,
                                position: 'insideLeft',
                                fill: COLORS.text.muted,
                                fontSize: 10,
                            }}
                        />
                        <Tooltip
                            contentStyle={{
                                background: COLORS.bg.card,
                                border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                            }}
                            formatter={(value, name) => [`${value.toFixed(1)}%`, name === 'pop_mean' ? 'Mean PoP' : name]}
                        />
                        <ReferenceLine
                            y={85}
                            stroke={COLORS.loss}
                            strokeDasharray="4 4"
                            label={{
                                value: '85% min',
                                fill: COLORS.loss,
                                fontSize: 9,
                                fontFamily: vortxTypography.mono,
                            }}
                        />
                        <Bar dataKey="pop_mean" radius={[4, 4, 0, 0]} fill={COLORS.accent}>
                            <ErrorBar
                                dataKey="pop_std"
                                width={8}
                                strokeWidth={1.5}
                                stroke={COLORS.text.secondary}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {data.map(d => (
                    <Typography key={d.name} sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        color: COLORS.text.muted,
                    }}>
                        {d.name}: {d.n_buildings} bldgs
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
